(function () {
  'use strict';
  if (customElements.get('bolodoo-cart-pairings')) return;
  const enqueue = action => {
    const job = (window.__bolodooCartMutationTail || Promise.resolve()).then(action, action);
    window.__bolodooCartMutationTail = job.catch(() => {});
    return job;
  };
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  const recommendations = new Map();

  class CartPairings extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.list = this.querySelector('[data-pairing-list]');
      this.panel = this.querySelector('[data-pairing-options]');
      this.select = this.querySelector('[data-pairing-variant]');
      this.shoeGroup = this.querySelector('[data-pairing-shoe-group]');
      this.shoeSize = this.querySelector('[data-pairing-shoe-size]');
      this.status = this.querySelector('[data-pairing-status]');
      this.addEventListener('click', event => {
        const target = event.target.closest('button');
        if (!target || this.busy) return;
        if (target.matches('[data-pairing-add]')) {
          const card = target.closest('[data-pairing-card]');
          const product = JSON.parse(card.querySelector('[data-pairing-product]').textContent);
          this.status.textContent = '';
          if (product.variants.length === 1 && !product.requiresShoeSize) this.add(product.variants[0], target, product);
          else this.choose(product, target);
        } else if (target.matches('[data-pairing-confirm]')) {
          const variant = this.product?.variants.find(item => String(item.id) === this.select.value);
          if (variant?.available) this.add(variant, this.trigger, this.product);
        } else if (target.matches('[data-pairing-close]')) this.closeOptions();
        else if (target.matches('[data-pairing-prev], [data-pairing-next]')) {
          this.list.scrollBy({ left: this.list.clientWidth * (target.matches('[data-pairing-prev]') ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.panel.hidden) {
          event.stopPropagation();
          this.closeOptions();
        }
      });
      this.list.addEventListener('scroll', () => this.arrows(), { passive: true });
      this.observer = new ResizeObserver(() => this.arrows());
      this.observer.observe(this.list);
      this.arrows();
      this.loadRecommendations();
    }

    disconnectedCallback() { this.observer?.disconnect(); }

    async loadRecommendations() {
      const url = this.dataset.recommendationUrl;
      if (!url || this.dataset.personalized === 'true') return;
      try {
        const key = `${url}|${this.dataset.cartProducts}`;
        let pending = recommendations.get(key);
        if (!pending) {
          pending = fetch(url, { headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(10000) }).then(response => {
            if (!response.ok) throw new Error('Recommendations unavailable');
            return response.text();
          });
          recommendations.set(key, pending);
          pending.catch(() => recommendations.delete(key));
        }
        const document = parse(await pending);
        const next = document.querySelector('bolodoo-cart-pairings[data-personalized="true"]');
        // Keep an active choice stable while the personalized row loads.
        if (!next || !this.isConnected || this.busy || !this.panel.hidden) return;
        next.querySelector('[data-pairing-status]').textContent = this.status.textContent;
        const scrollLeft = this.list.scrollLeft;
        const restoreFocus = this.contains(document.activeElement);
        this.replaceWith(next);
        next.querySelector('[data-pairing-list]').scrollLeft = scrollLeft;
        if (restoreFocus) next.querySelector('[data-pairing-heading]')?.focus({ preventScroll: true });
      } catch { /* Keep the available accessory fallback if recommendations fail. */ }
    }

    arrows() {
      const limit = this.list.scrollWidth - this.list.clientWidth;
      this.querySelector('[data-pairing-prev]').disabled = this.list.scrollLeft < 2;
      this.querySelector('[data-pairing-next]').disabled = this.list.scrollLeft >= limit - 2;
    }

    choose(product, trigger) {
      this.product = product;
      this.trigger = trigger;
      this.querySelector('[data-pairing-title]').textContent = product.title;
      this.select.replaceChildren(...product.variants.map(variant => {
        const option = new Option(`${variant.title} - ${variant.price}`, String(variant.id));
        option.disabled = !variant.available;
        return option;
      }));
      this.select.value = String(product.variants.find(variant => variant.id === product.selected && variant.available)?.id || product.variants.find(variant => variant.available).id);
      const label = this.querySelector('[data-pairing-variant-label]');
      if (label) label.textContent = product.requiresShoeSize ? 'Color' : 'Choose an option';
      if (this.shoeGroup && this.shoeSize) {
        this.shoeGroup.hidden = !product.requiresShoeSize;
        this.shoeSize.required = Boolean(product.requiresShoeSize);
        this.shoeSize.value = '';
        const chart = this.querySelector('[data-pairing-shoe-chart]');
        if (chart && product.requiresShoeSize) chart.replaceChildren(...[...this.shoeSize.options].filter(option => option.value).map(option => {
          const row = document.createElement('tr');
          [option.value, option.dataset.eu, option.dataset.length].forEach(value => { const cell = document.createElement('td'); cell.textContent = value; row.append(cell); });
          return row;
        }));
        const guide = this.shoeGroup.querySelector('details');
        if (guide) guide.open = false;
      }
      this.panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.select.focus({ preventScroll: true });
      this.panel.scrollIntoView({ block: 'nearest' });
    }

    closeOptions() {
      this.panel.hidden = true;
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.trigger?.focus({ preventScroll: true });
    }

    async add(variant, trigger, product = {}) {
      if (!variant?.available || this.busy) return;
      const item = { id: variant.id, quantity: 1 };
      if (product.requiresShoeSize) {
        const option = this.shoeSize?.selectedOptions[0];
        if (!option?.value || !option.dataset.eu) {
          this.shoeSize?.reportValidity();
          this.shoeSize?.focus();
          return;
        }
        item.properties = {
          'Shoe Size': `US ${option.value} / EU ${option.dataset.eu}`,
          '_Shoe Measurements': `EU ${option.dataset.eu}; Foot Length: ${option.dataset.length} cm; Width: ${option.dataset.width} cm`,
        };
      }
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('[data-pairing-add], [data-pairing-confirm]').forEach(button => { button.disabled = true; });
      this.status.textContent = 'Adding to your bag...';
      const root = (this.dataset.root || '/').replace(/\/?$/, '/');
      const request = async (suffix, body) => {
        const response = await fetch(`${root}cart${suffix}`, {
          method: body ? 'POST' : 'GET',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: body ? JSON.stringify(body) : undefined,
          signal: AbortSignal.timeout(12000),
        });
        const data = await response.json();
        if (!response.ok || data.status >= 400 || data.errors) throw new Error(typeof data.description === 'string' ? data.description : 'Unable to add this item. Please try again.');
        return data;
      };
      let addedToBag = false;
      try {
        await enqueue(async () => {
          const added = await request('/add.js', { items: [item], sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: location.pathname });
          addedToBag = true;
          const cart = await request('.js');
          const drawer = document.querySelector('cart-drawer');
          const current = drawer.querySelector('#CartDrawer');
          const next = added.sections?.['cart-drawer'] && parse(added.sections['cart-drawer']).querySelector('#CartDrawer');
          if (next) {
            current.innerHTML = next.innerHTML;
            current.classList.remove('notification-open');
            current.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
            const bubble = document.getElementById('cart-icon-bubble');
            const nextBubble = added.sections['cart-icon-bubble'] && parse(added.sections['cart-icon-bubble']).querySelector('.shopify-section');
            if (bubble && nextBubble) bubble.innerHTML = nextBubble.innerHTML;
          }
          // The add succeeded even if bundled section rendering is unavailable.
          const pairing = drawer.querySelector('bolodoo-cart-pairings');
          pairing?.exclude(cart);
          const status = pairing?.querySelector('[data-pairing-status]');
          if (status) status.textContent = 'Added to your bag.';
          document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart, source: 'bolodoo-cart-pairings' } }));
          if (!next) location.reload();
          else {
            drawer.open();
            const heading = pairing?.querySelector('[data-pairing-heading]');
            if (heading) heading.focus({ preventScroll: true });
          }
        });
      } catch (error) {
        if (addedToBag) { location.reload(); return; }
        this.status.textContent = error.name === 'TimeoutError' || error instanceof TypeError ? 'Please check your bag before trying again.' : error.message;
        trigger?.focus({ preventScroll: true });
      } finally {
        this.busy = false;
        this.setAttribute('aria-busy', 'false');
        this.querySelectorAll('[data-pairing-add], [data-pairing-confirm]').forEach(button => { button.disabled = false; });
      }
    }

    exclude(cart) {
      const ids = new Set(cart.items.map(item => String(item.product_id)));
      this.querySelectorAll('[data-pairing-card]').forEach(card => {
        if (ids.has(card.dataset.productId)) card.remove();
      });
      this.hidden = !this.querySelector('[data-pairing-card]');
      this.arrows();
    }
  }

  customElements.define('bolodoo-cart-pairings', CartPairings);
  document.addEventListener('cart:change', event => {
    const cart = event.detail?.cart || event.detail;
    if (!Array.isArray(cart?.items)) return;
    document.querySelectorAll('bolodoo-cart-pairings').forEach(element => element.exclude(cart));
  });
})();
